import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiFolder, FiExternalLink, FiShield, FiCloud, FiTool, FiTrendingUp, FiUsers, FiLock, FiServer, FiActivity } = FiIcons;

const Projects = ({ viewMode }) => {
  const [selectedProject, setSelectedProject] = useState(null);
  const [filter, setFilter] = useState('all');

  const projects = [
    {
      id: 1,
      title: "SOC 2 Compliance & Audit Framework",
      category: "compliance",
      description: "Leading the stand-up of the org-wide Office of Audit and Compliance, focusing on regulatory alignment and SOC 2 readiness.",
      longDescription: "Spearheaded the review, remediation, and build-out of M365 security compliance and controls across all company brands. This initiative focused on establishing a sustainable framework for obtaining and maintaining SOC 2 compliance, reinforcing client trust through rigorous security auditing.",
      image: "https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=600&h=400&fit=crop",
      tags: ["SOC 2", "NIST", "M365", "Audit Management", "Compliance"],
      impact: [
        "Established org-wide Office of Audit and Compliance",
        "Successful SOC 2 compliance remediation",
        "Standardized incident response annual simulations",
        "Unified compliance controls across multiple brands"
      ],
      technologies: ["M365 Security", "Purview", "Incident Response Tools", "Microsoft Entra"],
      duration: "2023 - Present",
      team: "Cross-brand Leadership",
      status: "Active"
    },
    {
      id: 2,
      title: "Enterprise M365 Cloud Migration",
      category: "cloud",
      description: "Orchestrating large-scale data and tenant migrations for acquisitions and cloud-to-cloud transitions.",
      longDescription: "Led critical data migrations for small, mid-sized, and large-scale acquisitions. Championed the cloud-to-cloud migration for M365 tenants and administered the global roll-out of Multi-Factor Authentication (MFA) and Conditional Access Policies for the entire organization.",
      image: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=600&h=400&fit=crop",
      tags: ["Cloud Migration", "MFA", "Azure AD", "SaaS Security", "Governance"],
      impact: [
        "100% MFA adoption across organization",
        "Seamless data migration for multiple acquisitions",
        "Implemented robust Conditional Access Policies",
        "Optimized Teams/SharePoint governance and retention"
      ],
      technologies: ["Azure AD / Entra ID", "SharePoint Online", "M365 Admin Center", "SaaS Security Services"],
      duration: "2021 - Present",
      team: "Senior Engineering Team",
      status: "Active"
    },
    {
      id: 3,
      title: "Secure Hybrid Infrastructure Design",
      category: "infrastructure",
      description: "Architecture and management of fully-clustered Hyper-V environments with 3-2-1 backup strategies.",
      longDescription: "Designed and managed a fully-clustered Hyper-V environment with sophisticated local and offsite backup strategies. Enacted comprehensive O365 security measures, including anti-phishing, anti-spam, and eDiscovery, while ensuring GDPR compliance through DLP policies.",
      image: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=600&h=400&fit=crop",
      tags: ["Hyper-V", "Networking", "GDPR", "DLP", "Firewalls"],
      impact: [
        "Zero data loss via 3-2-1 backup strategy",
        "Successful migration of internal IP to SharePoint/OneDrive",
        "Implemented enterprise-wide VPN and Network Policy Servers",
        "Maintained 99.9% uptime for core financial services"
      ],
      technologies: ["Microsoft Hyper-V", "Azure AD Connect", "InTune", "Cisco Firewalls", "VPN"],
      duration: "6 Years",
      team: "Lead Systems Admin",
      status: "Completed"
    },
    {
      id: 4,
      title: "PCI-DSS Hospitality Tech Overhaul",
      category: "compliance",
      description: "Modernizing hospitality infrastructure to meet strict PCI-DSS standards and eliminate legacy vulnerabilities.",
      longDescription: "Tasked with PCI-DSS compliance for a luxury hotel environment. This involved replacing the entire PBX phone system to eliminate non-compliant Windows servers and facilitating a multi-million dollar technology renovation while maintaining 24/7 hospitality operations.",
      image: "https://images.unsplash.com/photo-1566073771259-6a8506099945?w=600&h=400&fit=crop",
      tags: ["PCI-DSS", "PBX", "Vendor Management", "Project Management"],
      impact: [
        "Eliminated three non-compliant legacy servers",
        "Successful $2M infrastructure renovation",
        "Implemented barebones recovery and offsite storage",
        "Generated additional revenue via bandwidth upgrades"
      ],
      technologies: ["Windows Server", "PBX Systems", "Network Management", "PCI Compliance Tools"],
      duration: "1 Year",
      team: "Director of IT",
      status: "Completed"
    },
    {
      id: 5,
      title: "First Fully-Integrated Cisco Hotel",
      category: "infrastructure",
      description: "Orchestrating IT functions for the world's first fully-integrated Cisco-powered hospitality environment.",
      longDescription: "Managed complex vendor relationships and technical operations for a high-end hotel, spa, and restaurant. Migrated the majority of the server environment to Hyper-V and administered high-speed internet and interactive systems while managing a significant annual budget.",
      image: "https://images.unsplash.com/photo-1551816230-ef5deaed4a26?w=600&h=400&fit=crop",
      tags: ["Cisco Systems", "Hospitality Tech", "IP Networking", "Budgeting"],
      impact: [
        "Successfully virtualized physical server environment",
        "Managed $50K annual service budget",
        "Implemented IP-based room features and digital signage",
        "Maintained high-availability for luxury condo management"
      ],
      technologies: ["Cisco Systems", "Hyper-V", "GuestTek", "Symantec Backup"],
      duration: "4 Years",
      team: "IT Manager",
      status: "Completed"
    },
    {
      id: 6,
      title: "Military Systems Readiness (USAF)",
      category: "security",
      description: "Foundational experience in technical support and strict security protocols within the US Air Force.",
      longDescription: "Provided critical technical support for Air Force personnel and systems. This role involved maintaining system readiness and adhering to strict military security protocols, developing a career-long foundation in secure hardware and network troubleshooting.",
      image: "https://images.unsplash.com/photo-1544256718-3bcf237f3974?w=600&h=400&fit=crop",
      tags: ["Military Service", "Security Protocols", "Hardware", "Troubleshooting"],
      impact: [
        "Maintained 100% adherence to military security standards",
        "Optimized system readiness for personnel",
        "Developed foundational secure computing practices",
        "Honorably served in high-stakes environments"
      ],
      technologies: ["Military Network Hardware", "Desktop Support Systems", "Security Protocol Frameworks"],
      duration: "4 Years",
      team: "USAF Technical Unit",
      status: "Completed"
    }
  ];

  const categories = [
    { id: 'all', label: 'All Experience', icon: FiFolder },
    { id: 'compliance', label: 'Compliance', icon: FiShield },
    { id: 'cloud', label: 'Cloud', icon: FiCloud },
    { id: 'infrastructure', label: 'Infrastructure', icon: FiServer },
    { id: 'security', label: 'Security', icon: FiLock }
  ];

  const filteredProjects = filter === 'all' ? projects : projects.filter(p => p.category === filter);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Header */}
      <section className="section-padding bg-gradient-to-r from-gray-900 to-gray-800 text-white">
        <motion.div
          className="container mx-auto text-center"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1 className="font-heading text-5xl md:text-6xl font-bold mb-6">
            Career Milestones
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            {viewMode === 'professional' ? 
              "A journey through technical leadership, security engineering, and infrastructure transformation across diverse industries." : 
              "The highlights of 30+ years of breaking, building, and ultimately securing the digital world."}
          </p>
        </motion.div>
      </section>

      {/* Filter Tabs */}
      <section className="py-8 bg-white border-b">
        <div className="container mx-auto">
          <div className="flex flex-wrap justify-center gap-4">
            {categories.map((category) => (
              <motion.button
                key={category.id}
                onClick={() => setFilter(category.id)}
                className={`flex items-center px-6 py-3 rounded-xl font-medium transition-all duration-300 ${
                  filter === category.id ? 'bg-teal-600 text-white shadow-lg' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <SafeIcon icon={category.icon} className="w-4 h-4 mr-2" />
                {category.label}
              </motion.button>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="section-padding bg-gray-50">
        <div className="container mx-auto">
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <AnimatePresence>
              {filteredProjects.map((project) => (
                <motion.div
                  key={project.id}
                  variants={itemVariants}
                  layout
                  className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer"
                  whileHover={{ scale: 1.02, y: -5 }}
                  onClick={() => setSelectedProject(project)}
                >
                  <div className="relative overflow-hidden">
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-48 object-cover transition-transform duration-300 hover:scale-110"
                    />
                    <div className="absolute top-4 right-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        project.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                      }`}>
                        {project.status}
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="font-heading text-xl font-bold mb-3 text-gray-900">
                      {project.title}
                    </h3>
                    <p className="text-gray-600 mb-4 line-clamp-3">
                      {project.description}
                    </p>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {project.tags.slice(0, 3).map((tag, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-teal-100 text-teal-800 rounded-full text-xs font-medium"
                        >
                          {tag}
                        </span>
                      ))}
                      {project.tags.length > 3 && (
                        <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium">
                          +{project.tags.length - 3} more
                        </span>
                      )}
                    </div>
                    <div className="flex items-center justify-between text-sm text-gray-500">
                      <span>{project.duration}</span>
                      <span>{project.team}</span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>

      {/* Project Modal */}
      <AnimatePresence>
        {selectedProject && (
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedProject(null)}
          >
            <motion.div
              className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative">
                <img
                  src={selectedProject.image}
                  alt={selectedProject.title}
                  className="w-full h-64 object-cover"
                />
                <button
                  onClick={() => setSelectedProject(null)}
                  className="absolute top-4 right-4 w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-lg hover:bg-gray-100 transition-colors"
                >
                  ×
                </button>
              </div>
              <div className="p-8">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-heading text-3xl font-bold text-gray-900">
                    {selectedProject.title}
                  </h2>
                  <span className={`px-4 py-2 rounded-full font-medium ${
                    selectedProject.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                  }`}>
                    {selectedProject.status}
                  </span>
                </div>
                <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                  {selectedProject.longDescription}
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                  <div>
                    <h3 className="font-semibold text-xl mb-4 text-gray-900">Key Achievements</h3>
                    <ul className="space-y-3">
                      {selectedProject.impact.map((item, index) => (
                        <li key={index} className="flex items-start">
                          <SafeIcon icon={FiActivity} className="w-5 h-5 text-teal-600 mt-0.5 mr-3 flex-shrink-0" />
                          <span className="text-gray-700">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h3 className="font-semibold text-xl mb-4 text-gray-900">Core Technologies</h3>
                    <div className="flex flex-wrap gap-2">
                      {selectedProject.technologies.map((tech, index) => (
                        <span
                          key={index}
                          className="px-3 py-2 bg-gray-100 text-gray-800 rounded-lg font-medium"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 mb-8">
                  {selectedProject.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="px-4 py-2 bg-teal-100 text-teal-800 rounded-full font-medium"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="flex items-center justify-between text-gray-600 border-t pt-6">
                  <div className="flex items-center space-x-6">
                    <span><strong>Period:</strong> {selectedProject.duration}</span>
                    <span><strong>Role:</strong> {selectedProject.team}</span>
                  </div>
                  <div className="flex space-x-4">
                    <button 
                      onClick={() => setSelectedProject(null)}
                      className="flex items-center px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
                    >
                      Close Details
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Projects;